/**
 * Conserve les etats successifs de l'editeur.
 * Les piles sont copiees pour garder les anciennes versions intactes.
 * Un remplacement temporaire permet de suivre un glissement.
 */
/** Piles passe, present et futur d'un document. */
export interface THistory<T> { past: T[]; present: T; future: T[] }

/** Initialise une histoire sans etat anterieur. */
export const createHistory = <T>(initial: T): THistory<T> => ({ past: [], present: initial, future: [] });

/** Ajoute un nouvel etat, avec une limite sur les anciens etats. */
export const pushHistory = <T>(history: THistory<T>, next: T, limit = 100): THistory<T> => {
  if (next === history.present) return history;
  const past = [...history.past, history.present].slice(-Math.max(0, Math.trunc(limit)));
  return { past, present: next, future: [] };
};

/** Revient a l'etat precedent si possible. */
export const undoHistory = <T>(history: THistory<T>): THistory<T> => {
  if (history.past.length === 0) return history;
  return {
    past: history.past.slice(0, -1),
    present: history.past[history.past.length - 1],
    future: [history.present, ...history.future],
  };
};

/** Retablit le prochain etat si possible. */
export const redoHistory = <T>(history: THistory<T>): THistory<T> => {
  if (history.future.length === 0) return history;
  return {
    past: [...history.past, history.present],
    present: history.future[0],
    future: history.future.slice(1),
  };
};

/** Indique si un retour est disponible. */
export const canUndo = <T>(history: THistory<T>): boolean => history.past.length > 0;
/** Indique si un retablissement est disponible. */
export const canRedo = <T>(history: THistory<T>): boolean => history.future.length > 0;

/** Remplace l'etat courant sans toucher aux piles. */
export const replacePresent = <T>(history: THistory<T>, next: T): THistory<T> => ({
  ...history, present: next,
});
